import { useState } from "react";
import { CheckCircle2, XCircle, Flag, FlagOff, AlertTriangle, BookOpen } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { DifficultyBadge } from "./bits";
import { API, errMsg, TOPIC_NAMES } from "../lib/api";
import { toast } from "sonner";

const LETTERS = ["A", "B", "C", "D", "E"];
const REPORT_REASONS = ["Incorrect answer", "Incorrect explanation", "Clinical information may be outdated",
  "Typo", "Ambiguous question", "Duplicate question", "Other"];

export function ExplanationBlock({ feedback, selected }) {
  if (!feedback) return null;
  return (
    <div className="mt-6 space-y-5 animate-fade-up" data-testid="explanation-panel">
      <div className={`flex items-center gap-2.5 px-4 py-3 rounded-md border font-semibold text-sm ${
        feedback.correct ? "bg-success/10 border-success/30 text-success" : "bg-destructive/10 border-destructive/30 text-destructive"}`}>
        {feedback.correct
          ? <><CheckCircle2 className="w-5 h-5" /> Correct — well done.</>
          : <><XCircle className="w-5 h-5" /> Incorrect — the correct answer is {feedback.correct_answer}.</>}
      </div>
      <div className="bg-card border border-border rounded-lg p-5">
        <h4 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground mb-2">
          Why {feedback.correct_answer} is correct
        </h4>
        <p className="text-sm leading-relaxed">{feedback.explanation_correct}</p>
      </div>
      <div className="bg-card border border-border rounded-lg p-5 space-y-3">
        <h4 className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">Why the other options are wrong</h4>
        {LETTERS.filter((l) => l !== feedback.correct_answer).map((l) => (
          <div key={l} className="text-sm leading-relaxed" data-testid={`explanation-option-${l}`}>
            <span className={`font-semibold ${l === selected ? "text-destructive" : "text-foreground"}`}>
              {l}{l === selected ? " (your answer)" : ""}:
            </span>{" "}
            <span className="text-muted-foreground">{feedback.option_explanations?.[l] || "Not the best answer here."}</span>
          </div>
        ))}
      </div>
      {feedback.key_learning_point && (
        <div className="bg-secondary border border-border rounded-lg p-5 flex gap-3">
          <BookOpen className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div>
            <h4 className="text-xs tracking-[0.15em] uppercase font-semibold text-primary mb-1">Key learning point</h4>
            <p className="text-sm leading-relaxed">{feedback.key_learning_point}</p>
            {feedback.reference && <p className="mt-2 text-xs text-muted-foreground">Reference: {feedback.reference}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function QuestionPanel({
  question, index, total, selected, onSelect, submitted, feedback,
  onSubmit, onNext, isLast, submitting, mode, flagged, onToggleFlag,
}) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [reportSending, setReportSending] = useState(false);

  const sendReport = async () => {
    if (!reportReason) { toast.error("Please choose a reason"); return; }
    setReportSending(true);
    try {
      await API.post(`/questions/${question.id}/report`, { reason: reportReason, details: reportDetails });
      toast.success("Report sent to our clinical content team");
      setReportOpen(false);
      setReportReason(""); setReportDetails("");
    } catch (e) { toast.error(errMsg(e)); } finally { setReportSending(false); }
  };

  return (
    <div data-testid="question-panel">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground" data-testid="question-counter">
          Question {index + 1} of {total}
        </span>
        <span className="text-xs text-muted-foreground">·</span>
        <span className="text-xs font-medium text-muted-foreground">{TOPIC_NAMES[question.topic] || question.topic}</span>
        <DifficultyBadge level={question.difficulty} />
        <span className="ref text-xs text-muted-foreground ml-auto" data-testid="question-qid">{question.qid}</span>
      </div>

      <p className="text-base md:text-lg leading-relaxed font-medium" data-testid="question-stem">{question.stem}</p>

      <div className="mt-7 space-y-3" role="radiogroup" aria-label="Answer options">
        {LETTERS.map((l) => {
          const isSel = selected === l;
          let cls = "border-border bg-card hover:border-primary/50 hover:bg-muted/50";
          let icon = null;
          if (!submitted && isSel) cls = "border-primary bg-secondary ring-1 ring-primary";
          if (submitted && feedback) {
            if (l === feedback.correct_answer) {
              cls = "border-success bg-success/10";
              icon = <CheckCircle2 className="w-5 h-5 text-success shrink-0" />;
            } else if (isSel) {
              cls = "border-destructive bg-destructive/10";
              icon = <XCircle className="w-5 h-5 text-destructive shrink-0" />;
            } else {
              cls = "border-border bg-card opacity-70";
            }
          }
          return (
            <button key={l} type="button" data-testid={`answer-option-${l}`} disabled={submitted}
              onClick={() => onSelect(l)} aria-pressed={isSel}
              className={`w-full text-left flex items-start gap-4 px-5 py-4 rounded-lg border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${cls}`}>
              <span className={`w-7 h-7 rounded-full border flex items-center justify-center text-sm font-bold shrink-0 ${
                isSel && !submitted ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>
                {l}
              </span>
              <span className="text-sm md:text-base leading-relaxed flex-1">{question.options[l]}</span>
              {icon}
              {submitted && feedback && l === feedback.correct_answer && (
                <span className="sr-only">Correct answer</span>
              )}
              {submitted && feedback && isSel && l !== feedback.correct_answer && (
                <span className="sr-only">Your answer, incorrect</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {!submitted ? (
          <Button data-testid="submit-answer-btn" disabled={!selected || submitting} onClick={onSubmit} className="px-8">
            {submitting ? "Submitting…" : mode === "test" ? "Save answer" : "Submit answer"}
          </Button>
        ) : (
          <Button data-testid="next-question-btn" onClick={onNext} className="px-8">
            {isLast ? (mode === "test" ? "Finish test" : "See results") : "Next question"}
          </Button>
        )}
        <button type="button" data-testid="flag-question-btn" onClick={onToggleFlag}
          className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md border transition-colors ${
            flagged ? "border-accent bg-accent/20 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
          {flagged ? <FlagOff className="w-3.5 h-3.5" /> : <Flag className="w-3.5 h-3.5" />}
          {flagged ? "Flagged" : "Flag"}
        </button>
        <button type="button" data-testid="report-question-btn" onClick={() => setReportOpen(true)}
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors">
          <AlertTriangle className="w-3.5 h-3.5" /> Report this question
        </button>
      </div>

      {submitted && <ExplanationBlock feedback={feedback} selected={selected} />}

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Report question {question.qid}</DialogTitle>
            <DialogDescription>
              Reports go directly to our clinical content team for review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Select value={reportReason} onValueChange={setReportReason}>
              <SelectTrigger data-testid="report-reason-select"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
              <SelectContent>
                {REPORT_REASONS.map((r) => <SelectItem key={r} value={r} data-testid={`report-reason-${r}`}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="report-details-input" placeholder="Optional details — what looks wrong?"
              value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} rows={4} />
            <Button data-testid="report-submit-btn" onClick={sendReport} disabled={reportSending} className="w-full">
              {reportSending ? "Sending…" : "Send report"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
